'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * ThemedPortal — a portal whose host carries the `.ds` scope.
 *
 * ── Why this is not optional ─────────────────────────────────────────────────
 * Our tokens are scoped to `.ds` so the library can be adopted a subtree at a
 * time. A portal to `<body>` therefore lands OUTSIDE that scope and every
 * `--ds-*` token resolves to nothing — the layer falls back to whatever the
 * document provides, which in this app is a light theme with white cards. A
 * tooltip or menu would render white on black.
 *
 * Nothing that portals may ship without this.
 *
 * ── The host must be stable ──────────────────────────────────────────────────
 * Its node identity cannot change while layers are mounted, or React will
 * unmount and remount every layer. Created once, reused, never removed.
 *
 * ── Stacking context ─────────────────────────────────────────────────────────
 * The host must NOT create one. A `transform`, `filter` or stray `z-index` on it
 * would trap every layer beneath sibling content and re-create exactly the
 * z-index arms race the token scale exists to end.
 */

const HOST_ID = 'ds-portal-root';

/**
 * Which host a portal targets. The default is the `.ds`-scoped design-system host. The app's page-level layer host
 * (`#layer-root`, docs/THEME.md 2.10) passes `{ id: 'layer-root', className: '' }`: app popups must NOT inherit the
 * `--ds-*` scope.
 */
export interface PortalHost {
  id: string;
  className?: string;
}

const DS_HOST: PortalHost = { id: HOST_ID, className: 'ds' };

/** Find or create a stable host (created once, appended to body, never removed). Exported for lib/layers. */
export function ensurePortalHost(doc: Document, spec: PortalHost = DS_HOST): HTMLElement {
  const existing = doc.getElementById(spec.id);
  if (existing) return existing;
  const host = doc.createElement('div');
  host.id = spec.id;
  if (spec.className) host.className = spec.className;
  // No transform, no filter, no z-index — see above.
  host.style.position = 'relative';
  doc.body.appendChild(host);
  return host;
}

export function ThemedPortal({ children, host: spec }: { children: ReactNode; host?: PortalHost }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const id = spec?.id;
  const className = spec?.className;

  useEffect(() => {
    // ownerDocument rather than the global, so the library works inside an
    // iframe preview without silently portalling into the parent document.
    setHost(ensurePortalHost(document, id ? { id, className } : DS_HOST));
  }, [id, className]);

  // Null on the server and on the first client render — a portal target cannot
  // exist during SSR, and rendering into a guessed node causes a hydration
  // mismatch. Layers are client-only by nature, so nothing is lost.
  if (!host) return null;

  return createPortal(children, host);
}
